import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './MyOrdersPage.module.css';

import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';
import { useForceLogoutAndRedirect } from 'Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { OrderService } from '../../../Logic/OrderService';
import type { IOrderCardProps, OrderStatus } from '../../../Model/OrdersHistory.model';
import OrderStatusFilterDropdown from './OrderStatusFilterDropdown';
import OrdersList from './OrdersList';

const MyOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticationState } = UserAuthenticationStoreImplementation();
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const onAuthError = useForceLogoutAndRedirect();
  const token = authenticationState?.token?.trim() ?? '';
  const isLoggedIn = !!token;
  const service = new OrderService();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [orders, setOrders] = useState<IOrderCardProps[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<OrderStatus[]>([]);

  useEffect(() => {
    service.handleGetOrdersHistory({
      token,
      setLoading,
      setApiError,
      onAuthError,
      navigate,
      showPopup,
      setOrders
    });
  }, [isLoggedIn, token]);

  const filteredOrders = useMemo(
    () => service.filterOrdersByStatus(orders, selectedStatuses),
    [orders, selectedStatuses]
  );

  return (
    <main className={styles.page} data-testid="my-orders-page">
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.header}>
            <h2 className={styles.title} data-testid="my-orders-title">
              My orders
            </h2>
            <p className={styles.subtitle} data-testid="my-orders-subtitle">
              View your previous orders and their status.
            </p>
          </div>

          <div className={styles.filters} data-testid="my-orders-filters">
            <OrderStatusFilterDropdown
              value={selectedStatuses}
              onChange={setSelectedStatuses}
              disabled={loading || !isLoggedIn}
            />
          </div>
        </div>

        <OrdersList orders={filteredOrders} loading={loading} apiError={apiError} />
      </div>
    </main>
  );
};

export default MyOrdersPage;
